import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OrderDetailsWithPayment } from "./order-details-with-payment";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect, useCallback } from "react";
import { useOrders } from "@/api/orders";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import type { CarouselApi } from "@/components/ui/carousel";
import { Input } from "@/components/ui/input";
import { debounce } from "lodash";
import React from "react";
import { getPaymentStatusDisplay } from "@/lib/order-utils";

// Status filters removed since order status is now derived from shipments

// Helper type for photo metadata
interface PhotoMeta {
  url: string;
  taskType: string;
  status: string;
  time: string;
  notes?: string;
}

export function OrdersTable() {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const scope = JSON.stringify([carrierIds, dispatchCenterIds]);
  const [previousScope, setPreviousScope] = useState(scope);
  const [page, setPage] = useState(1);
  if (previousScope !== scope) {
    setPreviousScope(scope);
    setPage(1);
  }
  const [limit, setLimit] = useState(50);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselImages] = useState<PhotoMeta[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  // Status filter removed - should filter by shipment status instead
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const { data: ordersResponse, isLoading } = useOrders(
    previousScope === scope ? page : 1,
    limit,
    undefined, // Status filter removed
    debouncedSearchTerm || undefined,
    typeFilter,
    {
      carrierIds: carrierIds.join(","),
      dispatchCenterIds: dispatchCenterIds.join(","),
    }
  );

  // Removed handleOpenCarousel as photos are not part of Order model

  // Set up carousel API and handle slide changes
  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    // Define the select handler
    const onSelect = () => {
      // Now that we've checked carouselApi exists, TS should infer the types
      // If errors persist, it might indicate an issue with the CarouselApi type itself
      setCurrentImageIndex(carouselApi.selectedScrollSnap());
    };

    // Register the event listener
    carouselApi.on("select", onSelect);

    // Cleanup function to remove the listener
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]); // Dependency array remains the same

  // Debounce search term
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setDebouncedSearchTerm(term);
      setPage(1); // Reset to first page on new search
    }, 300),
    []
  );

  // Effect to handle search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);

    // Cleanup function to cancel debounce on unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  // Toggle order expansion
  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Handle search form submission (for Enter key)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Immediately trigger search on form submit
    setDebouncedSearchTerm(searchTerm);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by order number, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch(e)}
            className="pl-10"
          />
        </div>

        {/* Type Filter */}
        <Select
          value={typeFilter ?? "all"}
          onValueChange={(value) => {
            setTypeFilter(value === "all" ? undefined : value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="STANDARD">Standard</SelectItem>
            <SelectItem value="EXPRESS">Express</SelectItem>
            <SelectItem value="SAME_DAY">Same Day</SelectItem>
          </SelectContent>
        </Select>

        {/* Results per page selector */}
        <Select
          value={limit.toString()}
          onValueChange={(value) => {
            setLimit(parseInt(value, 10));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Per page" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10 per page</SelectItem>
            <SelectItem value="25">25 per page</SelectItem>
            <SelectItem value="50">50 per page</SelectItem>
            <SelectItem value="100">100 per page</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
            <span>Loading orders...</span>
          </div>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="rounded-md border overflow-x-auto">
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[150px]">Order #</TableHead>
                <TableHead className="min-w-[200px]">Customer</TableHead>
                <TableHead className="min-w-[150px]">Created</TableHead>
                <TableHead className="min-w-[150px]">Updated</TableHead>
                <TableHead className="min-w-[100px]">Shipments</TableHead>
                <TableHead className="min-w-[120px]">Payment</TableHead>
                <TableHead className="min-w-[200px]">Metadata</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(ordersResponse?.data ?? []).length > 0 ? (
                (ordersResponse?.data ?? []).map((order) => {
                  return (
                    <React.Fragment key={order.id}>
                      <TableRow
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleOrderExpansion(order.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <ChevronDown
                              className={`h-4 w-4 text-gray-400 transition-transform ${
                                expandedOrders.has(order.id) ? "rotate-180" : ""
                              }`}
                            />
                            <div>
                              <p className="font-medium">
                                ORD-{order.orderNumber}
                              </p>
                              {order.shipments &&
                                order.shipments.length > 0 && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {order.shipments.map(
                                      (
                                        s: { shipmentNumber?: number },
                                        i: number
                                      ) => (
                                        <span key={i}>
                                          {i > 0 && ", "}
                                          SHP-{s.shipmentNumber}
                                        </span>
                                      )
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {order.placerCustomer ? (
                            <div>
                              <p className="font-medium">
                                {order.placerCustomer.businessAccount
                                  ?.companyName ??
                                  `${order.placerCustomer.firstName ?? ""} ${
                                    order.placerCustomer.lastName ?? ""
                                  }`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {order.placerCustomer.email}
                              </p>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(order.createdAt).toLocaleTimeString()}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">
                            {new Date(order.updatedAt).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(order.updatedAt).toLocaleTimeString()}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {order.shipments?.length ?? 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {order.paymentStatus &&
                              (() => {
                                const display = getPaymentStatusDisplay(
                                  order.paymentStatus
                                );
                                return display.showBadge ? (
                                  <Badge
                                    variant="outline"
                                    className={display.className}
                                  >
                                    {display.label}
                                  </Badge>
                                ) : null;
                              })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px] text-xs text-gray-600">
                            {order.metadata ? (
                              <pre className="whitespace-pre-wrap">
                                {JSON.stringify(order.metadata, null, 2)}
                              </pre>
                            ) : (
                              <span className="text-gray-400">No metadata</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedOrders.has(order.id) && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <div className="p-4 bg-muted/50 rounded-lg">
                              <OrderDetailsWithPayment order={order} />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    {debouncedSearchTerm ? (
                      <div className="flex flex-col items-center gap-2">
                        <Search className="h-8 w-8 text-gray-400" />
                        <p className="text-gray-500">
                          No orders found for "{debouncedSearchTerm}"
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearSearch}
                        >
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-gray-500">No orders found</p>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Image Carousel Dialog */}
      <Dialog open={carouselOpen} onOpenChange={setCarouselOpen}>
        <DialogContent className="sm:max-w-5xl p-0 overflow-hidden max-h-[90vh] w-[95vw] sm:w-auto mx-auto">
          <DialogHeader className="p-4 border-b bg-gray-50 relative">
            <div className="flex items-center justify-between pr-8">
              <div className="flex flex-row items-center gap-3">
                <DialogTitle className="text-xl">Order Photos</DialogTitle>
                {carouselImages.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        carouselImages[currentImageIndex]?.taskType === "PICKUP"
                          ? "bg-indigo-600"
                          : carouselImages[currentImageIndex]?.taskType ===
                              "DELIVERY"
                            ? "bg-green-600"
                            : "bg-gray-500"
                      }
                    >
                      {carouselImages[currentImageIndex]?.taskType}
                    </Badge>
                    <Badge className="bg-gray-100 text-gray-800">
                      {carouselImages[currentImageIndex]?.status.replace(
                        /_/g,
                        " "
                      )}
                    </Badge>
                  </div>
                )}
              </div>
              {carouselImages.length > 0 && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    {new Date(
                      carouselImages[currentImageIndex]?.time
                    ).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    Image {currentImageIndex + 1} of {carouselImages.length}
                  </p>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="p-6 relative">
            <Carousel
              className="w-full"
              setApi={setCarouselApi}
              opts={{
                startIndex: 0,
                align: "center",
              }}
            >
              <CarouselContent>
                {carouselImages.map((image, index) => (
                  <CarouselItem key={index}>
                    <div className="flex flex-col items-center space-y-4">
                      <div className="relative w-full max-h-[70vh] overflow-hidden rounded-lg">
                        <img
                          src={image.url}
                          alt={`${image.taskType} proof image`}
                          className="mx-auto object-contain max-h-[70vh] w-full"
                          onError={(e) => {
                            console.error(`Failed to load image: ${image.url}`);
                            (e.target as HTMLImageElement).src =
                              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                          }}
                        />
                      </div>
                      {image.notes && (
                        <p className="text-sm bg-gray-50 p-2 rounded-md w-full">
                          Note: {image.notes}
                        </p>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Fixed position navigation buttons for better mobile visibility */}
              <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-10" />
              <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-10" />
            </Carousel>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {!isLoading && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-500">
            {debouncedSearchTerm ? (
              <>
                Found {ordersResponse?.metadata?.total ?? 0} results for "
                {debouncedSearchTerm}"
                {ordersResponse?.metadata?.total
                  ? ` (showing ${ordersResponse?.metadata?.total ? (page - 1) * limit + 1 : 0} to ${Math.min(page * limit, ordersResponse?.metadata?.total ?? 0)})`
                  : ""}
              </>
            ) : (
              <>
                Showing{" "}
                {ordersResponse?.metadata?.total ? (page - 1) * limit + 1 : 0}{" "}
                to{" "}
                {Math.min(page * limit, ordersResponse?.metadata?.total ?? 0)}{" "}
                of {ordersResponse?.metadata?.total ?? 0} results
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= (ordersResponse?.metadata?.totalPages ?? 0)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
